import { Injectable } from '@angular/core';
import { Carrito } from '../domain/carrito';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Tercero } from '../domain/tercero';
import { Observable } from 'rxjs';
import { GenericResponse } from '../domain/genericResponse';
import { GLOBAL } from '../global';
import { map } from 'rxjs/operators';
import { Parte } from '../domain/parte';
import { DetalleCarrito } from '../domain/detalle-carrito';
import { MatSnackBar } from '@angular/material';
import { UserSession } from '../domain/user-session';
import { ParteService } from './parte.service';
import { Direccion } from '../domain/direccion';

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService {

  public carrito: Carrito;
  private urlBaseCarrito: string;
  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private httpClient: HttpClient,
    public snackBar: MatSnackBar,
    private parteService: ParteService
  ) {
    this.urlBaseCarrito = GLOBAL.URL_BASE + 'sicaCarrito/';
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.getCarrito();
  }

  /**
   * Retorna la información del carrito de compras.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-20
   */
  getCarrito() {
    let cartList = localStorage.getItem(GLOBAL.LOCALSTORAGE_CARTLIST);

    if (!cartList) {
      cartList = localStorage.getItem(GLOBAL.LOCALSTORAGE_CARTLISTNOLOGIN);
    }

    if (cartList) {
      return this.carrito = JSON.parse(cartList);
    } else {
      const jwt = localStorage.getItem(GLOBAL.LOCALSTORAGE_SIGAMOSCARTTOKEN);

      if (!this.carrito) {
        if (jwt) {
          let userSession: UserSession = JSON.parse(jwt);
          this.carrito = new Carrito();
          this.carrito.tercId_SicaTercero = userSession.jti;
          this.carrito.totalItems = 0;
          this.carrito.valorTotal = 0;
          this.carrito.porcentajeDescuento = 0;
          this.carrito.valorDescuento = 0;
          this.carrito.listSicaDetalleCarrito = [];
        } else {
          this.carrito = new Carrito();
          this.carrito.totalItems = 0;
          this.carrito.porcentajeDescuento = 0;
          this.carrito.valorDescuento = 0;
          this.carrito.valorTotal = 0;
          this.carrito.listSicaDetalleCarrito = [];
        }
      }
    }

    return this.carrito;
  }

  /**
   * Retorna el detalle del carrito asociado a un cliente, y lo guarda en un localstorage.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-19
   * @param client 
   */
  findBySicaCarritoSicaTerceroTercIdAndEstado(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlBaseCarrito + 'findBySicaCarritoSicaTerceroTercIdAndEstado/', client, this.httpOptions)
      .pipe(
        map((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            this.carrito = data.lista[0];
            localStorage.setItem(GLOBAL.LOCALSTORAGE_CARTLIST, JSON.stringify(this.carrito));
          }
        })
      );
  }

  /**
   * Actualiza la información del carrito de compras.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-20
   */
  setCarrito(carrito: Carrito) {
    this.carrito = carrito
    localStorage.setItem(GLOBAL.LOCALSTORAGE_CARTLIST, JSON.stringify(this.carrito));
  }

  /**
   * Actualiza la información del carrito de compras sin iniciar sesión.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-28
   */
  setCarritoNoLogin(carrito: Carrito) {
    this.carrito = carrito
    localStorage.setItem(GLOBAL.LOCALSTORAGE_CARTLISTNOLOGIN, JSON.stringify(this.carrito));
  }

  /**
   * Actualiza los ítems del carrito de compras.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-22
   * @param carrito 
   */
  updateCarrito(carrito: Carrito): Observable<any> {
    return this.httpClient.post(this.urlBaseCarrito + 'updateCarrito/', carrito, this.httpOptions);
  }

  /**
   * Agrega un producto al carrito de compras.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-20
   * @param product Producto a agregar al carrito de compras.
   * @param isAuthenticate Es true si el usuario se encuentra autenticado, false de lo contrario.
   */
  public addToCart(product: Parte, isAuthenticate: boolean, first: boolean) {
    let message, status;
    if (first && this.carrito.listSicaDetalleCarrito.filter(item => item.partId == product.partId)[0]) {
      message = 'El Producto ' + product.nombre + ' ya se encuentra en el carrito.';
      status = 'error';
    } else {
      // FEDAZA: Obtengo la imagen del producto
      let productoConsultado = this.parteService.getDataParteConImagenes(product.partId, null, null, null, null, null, null, 0, 1, null)
        .subscribe((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            let parte: Parte = data.lista[0];

            let newDetalleCarrito: DetalleCarrito = new DetalleCarrito();
            newDetalleCarrito.partId = product.partId;
            if (first) {
              newDetalleCarrito.cantidad = parte.stock > 0 ? parte.unidadManejo : 0;
            } else {
              this.carrito.listSicaDetalleCarrito.forEach(i => {
                if (i.partId === product.partId) {
                  let availibilityCount: number = parte.stock - i.cantidad;
                  if (availibilityCount > 0) {
                    i.cantidad += parte.unidadManejo;
                    newDetalleCarrito.cantidad = i.cantidad;
                    i.disponible = availibilityCount;
                  } else {
                    this.snackBar.open('No se puede elegir más artículos de los disponibles. En stock ' + parte.stock + ' artículos.', '×', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
                  }
                }
              });
            }
            newDetalleCarrito.valorTotal = parte.precioVenta;
            newDetalleCarrito.nombre = parte.nombre;
            newDetalleCarrito.codigoParte = parte.codigo;
            newDetalleCarrito.disponible = parte.stock - parte.unidadManejo;
            newDetalleCarrito.imagenes = parte.imagenes;
            newDetalleCarrito.valorIva = parte.iva;
            if (isAuthenticate) {
              let cartList: Carrito = JSON.parse(localStorage.getItem(GLOBAL.LOCALSTORAGE_CARTLIST));
              if (first) {
                cartList.listSicaDetalleCarrito.push(newDetalleCarrito);
                cartList.valorTotal += parte.unidadManejo * Math.round(newDetalleCarrito.valorTotal + (newDetalleCarrito.valorTotal * newDetalleCarrito.valorIva / 100));
              } else {
                cartList.listSicaDetalleCarrito = this.carrito.listSicaDetalleCarrito;
                cartList.valorTotal += parte.unidadManejo * Math.round(newDetalleCarrito.valorTotal + (newDetalleCarrito.valorTotal * newDetalleCarrito.valorIva / 100));
              }
              // console.log(cartList.valorTotal);
              cartList.totalItems += parte.unidadManejo;
              this.carrito.carrId = cartList.carrId;
              this.carrito.tercId_SicaTercero = cartList.tercId_SicaTercero;
              this.carrito.costoEnvio = cartList.costoEnvio;
              this.carrito.estado = cartList.estado;
              this.carrito.fechaCreacion = cartList.fechaCreacion;
              this.carrito.fechaInicio = cartList.fechaInicio;
              this.carrito.fechaModificacion = cartList.fechaModificacion;
              this.carrito.totalItems = cartList.totalItems;
              this.carrito.usuarioCreador = cartList.usuarioCreador;
              this.carrito.usuarioModificador = cartList.usuarioModificador;
              this.carrito.valorIva = cartList.valorIva;
              this.carrito.valorTotal = cartList.valorTotal;
              this.carrito.listSicaDetalleCarrito = cartList.listSicaDetalleCarrito;
              this.setCarrito(this.carrito);
              this.updateCarrito(this.carrito).subscribe();
            } else {
              if (first) {
                this.getCarrito();
                this.carrito.listSicaDetalleCarrito.push(newDetalleCarrito);
                this.carrito.valorTotal += parte.unidadManejo * Math.round(newDetalleCarrito.valorTotal + (newDetalleCarrito.valorTotal * newDetalleCarrito.valorIva / 100));
              } else {
                this.carrito.valorTotal += parte.unidadManejo * Math.round(newDetalleCarrito.valorTotal + (newDetalleCarrito.valorTotal * newDetalleCarrito.valorIva / 100));
              }
              this.carrito.totalItems += parte.unidadManejo;
              this.setCarritoNoLogin(this.carrito);
            }
          }
        });

      message = 'El Producto ' + product.nombre + ' ha sido añadido al carrito.';
      status = 'success';
    }
    this.snackBar.open(message, '×', { panelClass: [status], verticalPosition: 'top', duration: 3000 });
  }

  /**
   * Elimina un producto del carrito de compras.
   * 
   * @author Camilo Delgado
   * @version 2019-01-24
   * @param product Producto a eliminar del carrito de compras.
   * @param isAuthenticate Es true si el usuario se encuentra autenticado, false de lo contrario.
   */
  public removeToCart(product: Parte, isAuthenticate: boolean) {
    let message, status;

    if (!this.carrito.listSicaDetalleCarrito.filter(item => item.partId == product.partId)[0]) {
      message = 'El Producto ' + product.nombre + ' no se encuentra en el carrito.';
      status = 'error';
    } else {
      // FEDAZA: Obtengo la imagen del producto
      let productoConsultado = this.parteService.getDataParteConImagenes(product.partId, null, null, null, null, null, null, 0, 1, null)
        .subscribe((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            let parte: Parte = data.lista[0];
            let availibilityCount: number = parte.stock;
            this.carrito.listSicaDetalleCarrito.forEach(i => {
              if (i.partId === product.partId) {
                if (i.cantidad > parte.unidadManejo) {
                  i.cantidad -= parte.unidadManejo;
                  i.disponible = availibilityCount;
                  this.carrito.totalItems -= parte.unidadManejo;
                  this.carrito.valorTotal -= Math.round(i.valorTotal + (i.valorTotal * i.valorIva / 100));
                  this.carrito.valorIva -= Math.round(i.valorTotal * i.valorIva / 100);
                  message = 'El Producto ' + product.nombre + ' ha sido disminuido del carrito.';
                  status = 'success';
                } else {
                  const index: number = this.carrito.listSicaDetalleCarrito.indexOf(i);
                  this.carrito.listSicaDetalleCarrito.splice(index, 1);
                  this.carrito.valorTotal -= i.cantidad * Math.round(i.valorTotal + (i.valorTotal * i.valorIva / 100));
                  this.carrito.totalItems -= i.cantidad;
                  message = 'El Producto ' + product.nombre + ' ha sido eliminado del carrito.';
                  status = 'success';
                }
                this.snackBar.open(message, '×', { panelClass: [status], verticalPosition: 'top', duration: 3000 });
              }
            });

            if (isAuthenticate) {
              this.setCarrito(this.carrito);
              this.updateCarrito(this.carrito).subscribe();
            } else {
              this.setCarritoNoLogin(this.carrito);
            }
          }
        });
    }
  }

  /**
   * Retorna el total de los productos en el carrito.
   * 
   * @author Frank Edward Daza González
   * @version 2018-11-26
   * @param detalleCarrito 
   */
  getTotalItemCart(detalleCarrito: DetalleCarrito[]): number {
    let totalItems = 0;

    detalleCarrito.forEach(element => {
      totalItems += element.cantidad;
    });

    return totalItems;
  }

  /**
   * Método en donde crea un pedido con las partes que se encuentren en el carrito.
   * Se envía la dirección para calcular costos de flete.
   * @author Camilo Delgado
   * @param direccion 
   * @return el pedido con los valores comprometidos y la lista de detalles comprometidas.
   */
  comprometerPedidoEnCarrito(direccion: Direccion): Observable<any> {
    return this.httpClient.post(this.urlBaseCarrito + '/comprometerPedidoEnCarrito', direccion, this.httpOptions)
  }
}
